import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PaymentAgentDepositTransaction } from 'api/Transaction/Types/PaymentAgentDepositTransaction'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { CampaignWithTradingAccount } from '../Deposit/DepositForm'
import { PaymentAgentDepositForm, PaymentDepositFormValues } from './PaymentAgentDepositForm'

interface Props {
  client: ClientDto | null
  setClient(): void
}

const PaymentAgentDepositPage: React.FC<Props> = ({ client, setClient }) => {
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [clientWallets, setClientWallets] = useState<WalletDto[]>([])
  const [paymentAgentWallets, setPaymentAgentWallets] = useState<WalletDto[]>([])
  const [clientWallet, setClientWallet] = useState<WalletDto>()
  const [clientTradingAccounts, setClientTradingAccounts] = useState<TradingAccountDto[]>([])

  const [paymentAgentWalletId, setPaymentAgentWalletId] = useState('')
  const [paymentAgentClientId, setPaymentAgentClientId] = useState('')
  const [form, setForm] = useState<PaymentDepositFormValues>()
  const [confirmationData, setConfirmationData] = useState<PaymentAgentDepositTransaction>()
  const [isConfirmationModalOpen, setConfirmationModal] = useState<boolean>(false)
  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (client?.id) {
      return await walletsApiClient.getPaymentAgentsByClientId(client.id)
    }
  }, [client]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: paymentAgents, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    getClientWallets()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClientWallets = async () => {
    if (client?.id) {
      const response = await walletsApiClient.getClientWallet(client?.id)
      setClientWallets(response)
    }
  }

  const onSubmit = async (values: PaymentAgentDepositTransaction) => {
    setConfirmationData(values)
    setConfirmationModal(true)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const response =
          await walletsApiClient.createPaymentAgentDepositTransactionForClient(confirmationData)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: `Deposit has been submitted.`,
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: response.id,
          }),
        })
        await fetchClientWallets()
        forceRefresh()
        setConfirmationModal(false)
      } catch (e) {
        console.error(e)
        setConfirmationModal(false)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModal(false)
  }

  const getClientWallets = async () => {
    setLoading(true)
    await fetchClientWallets()
    setLoading(false)
  }

  const getPaymentAgentWallets = async (paymentAgentId: string) => {
    setLoading(true)
    if (paymentAgentId) {
      const response = await walletsApiClient.getClientWallet(paymentAgentId)
      setPaymentAgentWallets(response)
    } else {
      setPaymentAgentWallets([])
    }
    setLoading(false)
  }

  const setClientWalletByWalletId = (walletId: string) => {
    const wallet = (clientWallets || []).find((x) => x.id === walletId)
    if (wallet?.currency?.id) {
      setCurrency(wallet?.currency?.id)
    }
    setClientWallet(wallet)
  }

  const getCampaignByWalletId = async (walletId: string) => {
    setLoading(true)
    const response = await tradingAccountsApiClient.getCampaignsForDepositClient(walletId)
    setClientTradingAccounts(response.items)
    setLoading(false)
  }

  const selectFromPaymentAgentName = () => {
    return paymentAgents?.find((x) => x?.methodId === form?.paymentAgentMethodId)?.name
  }

  const selectFromWalletName = () => {
    return paymentAgentWallets?.find((x) => x.id === confirmationData?.paymentAgentWalletId)?.name
  }

  const selectToWalletName = () => {
    return clientWallets?.find((x) => x.id === confirmationData?.clientWalletId)?.name
  }

  const getCampaignsFields = (): CampaignWithTradingAccount[] => {
    // from requirements - {campaigns.{firstElementOfArray}.name}
    return clientTradingAccounts.reduce<CampaignWithTradingAccount[]>((prev, curr) => {
      const dto: CampaignWithTradingAccount = {
        name: curr.campaigns[0].name,
        id: curr.campaigns[0].id,
        tradingAccountName: curr.name,
        tradingAccountId: curr.id,
      }
      return [...prev, dto]
    }, [] as CampaignWithTradingAccount[])
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to transfer fund from the payment agent {selectFromPaymentAgentName()}
                , {selectFromWalletName()} to the client {client?.name}, {selectToWalletName()}. The
                parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Payment Agent Method:</strong> {selectFromPaymentAgentName()}
                </div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>From Payment Agent Wallet:</strong> {selectFromWalletName()}
                </div>
                <div id='field'>
                  <strong>To Client Wallet:</strong> {selectToWalletName()}
                </div>
                <div id='field'>
                  <strong>Bank name:</strong> {form?.system}
                </div>
                <div id='field'>
                  <strong>IBAN :</strong> {form?.account}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {confirmationData?.amount} {currency}
                </div>
              </div>
            </div>
          }
        />
      )}
      <PaymentAgentDepositForm
        isLoading={loading}
        paymentAgents={paymentAgents || []}
        getPaymentAgentWallets={getPaymentAgentWallets}
        getClientWallets={getClientWallets}
        setClientWalletId={setClientWalletByWalletId}
        getCampaignByWallet={getCampaignByWalletId}
        clientWallet={clientWallet}
        paymentAgentWalletId={paymentAgentWalletId}
        setPaymentAgentWalletId={setPaymentAgentWalletId}
        paymentAgentClientId={paymentAgentClientId}
        setPaymentAgentClientId={setPaymentAgentClientId}
        currency={currency}
        client={client}
        setClient={setClient}
        clientWallets={clientWallets || []}
        paymentAgentWallets={paymentAgentWallets || []}
        setForm={setForm}
        onSubmit={onSubmit}
        campaigns={getCampaignsFields()}
      />
    </React.Fragment>
  )
}

export default PaymentAgentDepositPage
